"use client";

import { AnimatePresence, motion } from "motion/react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { cn } from "../../lib/utils";
import { Heading } from "../heading";
import { Image } from "../media/image";

type Testimonial = {
  name: string;
  designation: string;
  quote: string;
  image: string | null;
  alt: string | null;
};

function calculateGap(width: number) {
  const minWidth = 1024;
  const maxWidth = 1456;
  const minGap = 60;
  const maxGap = 86;
  if (width <= minWidth) return minGap;
  if (width >= maxWidth)
    return Math.max(minGap, maxGap + 0.060_18 * (width - maxWidth));
  return (
    minGap + (maxGap - minGap) * ((width - minWidth) / (maxWidth - minWidth))
  );
}

export function CircularTestimonyCarousel({
  title,
  testimonials,
  autoplay = false,
  textColorClasses,
  ...additionalProperties
}: {
  title: string | undefined;
  testimonials: Testimonial[];
  autoplay?: boolean;
  textColorClasses?: string;
}) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [containerWidth, setContainerWidth] = useState(1200);
  const imageContainerReference = useRef<HTMLDivElement>(null);
  const autoplayIntervalReference = useRef<NodeJS.Timeout | null>(null);

  const testimonialsLength = useMemo(() => testimonials.length, [testimonials]);
  const activeTestimonial = useMemo(
    () => testimonials[activeIndex],
    [activeIndex, testimonials],
  );

  // Responsive gap calculation
  useEffect(() => {
    function handleResize() {
      if (imageContainerReference.current) {
        setContainerWidth(imageContainerReference.current.offsetWidth);
      }
    }
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Autoplay
  useEffect(() => {
    if (autoplay) {
      autoplayIntervalReference.current = setInterval(() => {
        setActiveIndex((previous) => (previous + 1) % testimonialsLength);
      }, 5000);
    }
    return () => {
      if (autoplayIntervalReference.current)
        clearInterval(autoplayIntervalReference.current);
    };
  }, [autoplay, testimonialsLength]);

  // Navigation handlers
  const handleNext = useCallback(() => {
    setActiveIndex((previous) => (previous + 1) % testimonialsLength);
    if (autoplayIntervalReference.current)
      clearInterval(autoplayIntervalReference.current);
  }, [testimonialsLength]);

  const handlePrevious = useCallback(() => {
    setActiveIndex(
      (previous) => (previous - 1 + testimonialsLength) % testimonialsLength,
    );
    if (autoplayIntervalReference.current)
      clearInterval(autoplayIntervalReference.current);
  }, [testimonialsLength]);

  // Keyboard navigation
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") handlePrevious();
      if (e.key === "ArrowRight") handleNext();
    };
    globalThis.addEventListener("keydown", handleKey);
    return () => globalThis.removeEventListener("keydown", handleKey);
  }, [handleNext, handlePrevious]);

  // Compute transforms for each image (always show 3: left, center, right)
  function getImageStyle(index: number): React.CSSProperties {
    const gap = calculateGap(containerWidth);
    const maxStickUp = gap * 0.8;
    const isActive = index === activeIndex;
    const isLeft =
      (activeIndex - 1 + testimonialsLength) % testimonialsLength === index;
    const isRight = (activeIndex + 1) % testimonialsLength === index;

    if (isActive) {
      return {
        zIndex: 3,
        opacity: 1,
        pointerEvents: "auto",
        transform: `translateX(0px) translateY(0px) scale(1) rotateY(0deg)`,
        transition: "all 0.8s cubic-bezier(.4,2,.3,1)",
      };
    }
    if (isLeft) {
      return {
        zIndex: 2,
        opacity: 1,
        pointerEvents: "auto",
        transform: `translateX(-${gap}px) translateY(-${maxStickUp}px) scale(0.85) rotateY(15deg)`,
        transition: "all 0.8s cubic-bezier(.4,2,.3,1)",
      };
    }
    if (isRight) {
      return {
        zIndex: 2,
        opacity: 1,
        pointerEvents: "auto",
        transform: `translateX(${gap}px) translateY(-${maxStickUp}px) scale(0.85) rotateY(-15deg)`,
        transition: "all 0.8s cubic-bezier(.4,2,.3,1)",
      };
    }
    // Hide all other images
    return {
      zIndex: 1,
      opacity: 0,
      pointerEvents: "none",
      transition: "all 0.8s cubic-bezier(.4,2,.3,1)",
    };
  }

  // Framer Motion variants for quote - optimized for performance
  const quoteVariants = {
    initial: { opacity: 0, y: 12 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -12 },
  };

  return (
    <div
      {...additionalProperties}
      className={cn("mx-auto w-full max-w-4xl px-8 py-4", textColorClasses)}
    >
      {title && (
        <Heading variant="h2" className="mb-8 text-left">
          {title}
        </Heading>
      )}
      <div className="grid gap-20 md:grid-cols-2 py-8">
        {/* Images */}
        <div
          ref={imageContainerReference}
          className="relative h-96 w-full"
          style={{ perspective: "1000px" }}
        >
          {testimonials.map((testimonial, index) => {
            if (!testimonial.image) return null;
            return (
              <div
                key={testimonial.image ?? index}
                className="absolute inset-0"
                style={{
                  ...getImageStyle(index),
                  willChange: "transform, opacity",
                  backfaceVisibility: "hidden",
                  WebkitBackfaceVisibility: "hidden",
                }}
              >
                <Image
                  src={testimonial.image}
                  alt={testimonial.alt ?? testimonial.name}
                  fill
                  className="h-full w-full rounded-2xl object-cover"
                  style={{
                    boxShadow: "0 10px 30px rgba(0, 0, 0, 0.2)",
                  }}
                  draggable={false}
                />
              </div>
            );
          })}
        </div>
        {/* Content */}
        <div className="flex flex-col justify-between py-4">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeIndex}
              variants={quoteVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={{
                duration: 0.25,
                ease: [0.22, 1, 0.36, 1],
              }}
              style={{ willChange: "opacity, transform" }}
            >
              <h3 className={cn("text-2xl font-bold mb-1", textColorClasses)}>
                {activeTestimonial.name}
              </h3>
              <p className={cn("text-sm mb-8", textColorClasses)}>
                {activeTestimonial.designation}
              </p>
              <motion.p
                className={cn("text-lg leading-relaxed", textColorClasses)}
              >
                {activeTestimonial.quote.split(" ").map((word, index) => (
                  <motion.span
                    key={index}
                    initial={{
                      opacity: 0,
                      y: 8,
                    }}
                    animate={{
                      opacity: 1,
                      y: 0,
                    }}
                    transition={{
                      duration: 0.2,
                      ease: [0.22, 1, 0.36, 1],
                      delay: 0.015 * index,
                    }}
                    className="inline-block"
                    style={{ willChange: "opacity, transform" }}
                  >
                    {word}&nbsp;
                  </motion.span>
                ))}
              </motion.p>
            </motion.div>
          </AnimatePresence>
          <div className="flex gap-6 pt-12 md:pt-0">
            <button
              onClick={handlePrevious}
              className="bg-secondary group/button flex h-11 w-11 items-center justify-center rounded-full cursor-pointer transition-colors duration-300 hover:bg-primary"
              aria-label="Previous testimonial"
            >
              <ArrowLeft className="text-foreground h-7 w-7 transition-transform duration-300 group-hover/button:rotate-12" />
            </button>
            <button
              onClick={handleNext}
              className="bg-secondary group/button flex h-11 w-11 items-center justify-center rounded-full cursor-pointer transition-colors duration-300 hover:bg-primary"
              aria-label="Next testimonial"
            >
              <ArrowRight className="text-foreground h-7 w-7 transition-transform duration-300 group-hover/button:-rotate-12" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
